import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useScroll, useMotionValue } from "framer-motion";
import "../../styles/components/timeline-responsive.css";


const PathFollower = ({ d, progress, ballSize = 24 }) => {
  const pathRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
      const pt = pathRef.current.getPointAtLength(0);
      x.set(pt.x - ballSize / 2);
      y.set(pt.y - ballSize / 2);
    }
  }, [d, x, y, ballSize]);

  useEffect(() => {
    if (!progress || !pathRef.current) return;
    
    const unsub = progress.on("change", (t) => {
      if (!pathRef.current) return;
      const L = Math.max(0, Math.min(1, t)) * pathLength;
      const pt = pathRef.current.getPointAtLength(L);
      x.set(pt.x - ballSize / 2);
      y.set(pt.y - ballSize / 2);
    });
    
    return () => unsub && unsub();
  }, [pathLength, progress, x, y, ballSize]);

  return (
    <>
      <defs>
        <linearGradient id="timeline-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#206AEC" />
          <stop offset="50%" stopColor="#00B140" />
          <stop offset="100%" stopColor="#206AEC" />
        </linearGradient>
        <linearGradient id="ballGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#206AEC" />
          <stop offset="50%" stopColor="#00B140" />
          <stop offset="100%" stopColor="#206AEC" />
        </linearGradient>
      </defs>
      <path 
        ref={pathRef} 
        d={d} 
        stroke="url(#timeline-gradient)" 
        strokeWidth="4" 
        fill="none" 
        opacity="0.3" 
      />
      <motion.circle
        cx={x}
        cy={y}
        r={ballSize / 2}
        fill="url(#ballGradient)"
        animate={{
          scale: [1, 1.2, 1],
          filter: [
            "drop-shadow(0 0 10px rgba(32, 106, 236, 0.6))",
            "drop-shadow(0 0 20px rgba(0, 177, 64, 0.7))",
            "drop-shadow(0 0 10px rgba(32, 106, 236, 0.6))"
          ]
        }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      />
    </>
  );
};

function TimelineSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const timelineRef = useRef(null);
  
  const timeline = [
    {
      year: "2012",
      items: [
        "GEOTECH establishes its wings",
        "Strategic partnership with Bentley for South India operations",
      ],
    },
    {
      year: "2013",
      items: [
        "GEOTECH Geospatial Pvt Ltd established",
        "GEOTECH was certified ISO 9001:2008 Company",
      ],
    },
    {
      year: "2014",
      items: [
        "Strategic Partnership with Intergraph / Hexagon for the supply of Geo / Photogrammetry products",
      ],
    },
    {
      year: "2016",
      items: [
        "GEOTECH Entered strategic partnership with Esri India for ArcGIS products",
      ],
    },
    {
      year: "2017",
      items: ["GEOTECH was awarded ISO 9001 : 2015 certification"]
    },
    {
      year: "2018",
      items: [
        "GEOTECH bagged order from Karnataka Public Works Department",
        "GEOTECH bagged order from Greater Chennai Corporation",
        "GEOTECH was awarded Certificate of Registration of Trade Mark no. 2616525",
      ],
    },
    {
      year: "2019",
      items: ["Awarded Partner of the year 2019 by Esri India"]
    },
    {
      year: "2020",
      items: [
        "GEOTECH bagged Order from National Centre for Sustainable Coastal Zone Management",
        "GEOTECH bagged Integrated Transport Management System Project from Trivandrum Smart City",
      ],
    },
    {
      year: "2021",
      items: [
        "Awarded Partner of the year - 2021 - Business Impact by Esri India",
        "GEOTECH was awarded ISO 27001:2015 Certification",
      ],
    },
    {
      year: "2022",
      items: [
        "GEOTECH was awarded ISO 27001:2022 Certification",
        "Strategic partnership with Idea forge, Here Maps, Apache, Deep Trekker, Planet",
        "GEOTECH establishes its Singapore operations",
        "GEOTECH establishes its Service Division",
        "GEOTECH bagged  Geotagging project from Department of Agriculture",
        "Strategic MoU with Tamil Nadu Agricultural University",
      ]
    },
    {
      year: "2023",
      items: [
        "GEOTECH bagged GIS Dashboard from TN Forest Department",
        "GEOTECH bagged order from CMDA",
        "GEOTECH bagged first PLANET SCOPE IMAGERY order from TNAU",
        "ESRI Award - Partner of the year - Business Impact",
        "Signed MoU with Anna University",
        "Industry Outlook - Top 10 GIS Service Providers",
      ]
    },
    {
      year: "2024",
      items: [
        "Geotech was awarded CMMI – Level 3 Certification",
        "The Industrial Review - Excellence in providing Advanced GIS Solutions",
        "The Industrial Review - Best Technology Partner",
        "Business Connect - Company of the year award",
        "Industry Outlook - Top 10 GIS Service Providers",
        "SiliconIndia - 10 Most Promising GIS Service Providers",
        "CIO Media - Most Prominent GIS Solution Provider",
      ]
    },
    {
      year: "2025",
      items: [
        "InnerReview -Top 10 Inspiring Leaders in IT",
        "IndustryOpinion - The 10 Most Promising Companies to focus in 2025",
        "Geotech bagged order from Chennai Metropolitan Development Authority",
        "Geotech bagged service project from Tamil Nadu Pollution Control Board",
        "Geotech bagged orders from National Centre for Coastal Research",
      ],
    },
  ];

  const generateTimelinePath = useCallback((itemCount, mobile = false) => {
    const rowHeight = 350; // Height for each row
    const startY = 100;
    const centerX = 600;
    const curveWidth = mobile ? 250 : 200;
    
    if (itemCount <= 0) {
      return `M ${centerX} ${startY}`;
    }
    
    if (mobile) {
      const endY = startY + (itemCount - 1) * rowHeight;
      return `M ${centerX} ${startY} L ${centerX} ${endY}`;
    }
    
    let path = `M ${centerX} ${startY}`;
    
    for (let i = 0; i < itemCount; i++) {
      const currentY = startY + (i * rowHeight);
      const nextY = startY + ((i + 1) * rowHeight);
      const midY = currentY + (rowHeight / 2);
      const isLeftCurve = i % 2 === 0;
      const controlX = isLeftCurve ? centerX - curveWidth : centerX + curveWidth;
      path += ` Q ${controlX} ${midY}, ${centerX} ${nextY}`;
    }
    
    return path;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [timelinePath, setTimelinePath] = useState(generateTimelinePath(timeline.length, isMobile));
  const totalHeight = Math.max(timeline.length * 350 + 200, 1000); // Minimum height of 1000px
  
  useEffect(() => {
    const newPath = generateTimelinePath(timeline.length, isMobile);
    setTimelinePath(newPath);
  }, [isMobile, generateTimelinePath, timeline.length]);

  const { scrollYProgress } = useScroll({ 
    target: timelineRef, 
    offset: ['start center', 'end center'] 
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      
      const blocks = timelineRef.current.querySelectorAll('.timeline-card');
      if (blocks.length === 0) return;
      
      const scrollY = window.scrollY;
      const viewportCenter = scrollY + window.innerHeight / 2;

      let newActiveIndex = 0;
      let minDistance = Infinity;

      blocks.forEach((block, index) => {
        const rect = block.getBoundingClientRect();
        const blockCenter = rect.top + window.scrollY + rect.height / 2;
        const distance = Math.abs(viewportCenter - blockCenter);

        if (distance < minDistance) {
          minDistance = distance;
          newActiveIndex = index;
        }
      });

      setActiveIndex(newActiveIndex);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="timeline-section">
      <div className="section__container-abt">
        <p className="timeline-section__slogan">
          In this journey to success, GEOTECH's history has been punctuated with several distinguished events, a few of which are as follows:
        </p>
        
        <h2 className="section__title--boxed">
          Read about the main milestones in GEOTECH's Timeline
        </h2>
        
        <div className="timeline" ref={timelineRef}>
          {/* SVG Path */}
          <svg 
            className="timeline-svg" 
            viewBox={`0 0 1200 ${totalHeight}`}
            height={totalHeight}
            preserveAspectRatio="xMidYMid meet"
          >
            <PathFollower 
              d={timelinePath} 
              progress={scrollYProgress} 
              ballSize={24} 
            />
          </svg>
          
          {/* Timeline Cards */}
          <div className="timeline-cards-container" style={{ minHeight: `${totalHeight}px` }}>
            {timeline.map((item, index) => {
              const topPosition = 100 + (index * 350);
              
              return (
                <motion.div
                  key={item.year}
                  className={`timeline-card ${activeIndex === index ? 'active' : ''}`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  style={isMobile ? {} : {
                    top: `${topPosition}px`,
                  }}
                >
                  {/* Connection Dot */}
                  <div className={`timeline__dot ${activeIndex === index ? 'active' : ''}`} />
                  
                  {/* Year Badge */}
                  <div className="timeline__year">
                    {item.year}
                  </div>
                  
                  {/* Events */}
                  <div className="timeline__events">
                    {item.items.map((event, idx) => (
                      <p key={idx} className="timeline__text">
                        {event}
                      </p>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TimelineSection;